/**
 * Command K — the keyboard model, as one pure function.
 *
 * `mapKey(event, ctx)` turns a keystroke into an INTENT. It never touches the
 * DOM, never reads a signal and never decides what an intent does — so the
 * whole keymap is a truth table a spec can walk exhaustively, which is the
 * only way a palette with this many bindings stays honest.
 *
 * AI CONTEXT — why this cannot live in HotkeysService
 *
 * `HotkeysService.handleKeydown` (`core/services/hotkeys.service.ts:149`)
 * returns immediately unless ⌘/Ctrl is held, and its map is keyed `mod+<key>`.
 * Bare keys, `⇧↵` and `⌥Space` therefore have no home in it — three of them
 * are already stored-and-never-dispatched at `schedule-commands.service.ts:129`.
 * It also listens on `document` at CAPTURE phase and `stopPropagation()`s on
 * match, so a bubble-phase handler inside the palette can never claim ⌘K, ⌘↵
 * or ⌘1–9 back from it.
 *
 * `CmdkKeymapService` therefore takes a `hotkeys.suspend()` lease for the
 * palette's open lifetime and binds its own capture-phase listener. This file
 * is the decision layer for that listener.
 *
 * The ESC LADDER is deliberately NOT resolved here. `mapKey` emits a single
 * `escape` intent and `CmdkStateService.escape()` owns the precedence, so
 * there is exactly one place that knows whether Esc stops voice, pops a flow,
 * narrows the answer or closes the palette.
 */

/** Every action the palette's keyboard layer can ask for. */
export type CmdkIntent =
  | { readonly type: 'escape' }
  | { readonly type: 'move'; readonly delta: number }
  | { readonly type: 'move-edge'; readonly edge: 'first' | 'last' }
  | { readonly type: 'activate' }
  | { readonly type: 'instant-book' }
  | { readonly type: 'ask' }
  | { readonly type: 'continue-in-chat' }
  | { readonly type: 'route-ai' }
  | { readonly type: 'toggle-answer-room' }
  | { readonly type: 'accept-ghost' }
  | { readonly type: 'open-action-menu' }
  | { readonly type: 'close-action-menu' }
  | { readonly type: 'action-menu-move'; readonly delta: number }
  | { readonly type: 'action-menu-activate' }
  | { readonly type: 'cycle-scope'; readonly delta: number }
  | { readonly type: 'quick-open'; readonly index: number }
  | { readonly type: 'copy-link' }
  | { readonly type: 'clear-prefix' }
  | { readonly type: 'help' }
  | { readonly type: 'toggle-voice' }
  | { readonly type: 'close-palette' };

/** The minimum a `KeyboardEvent` must look like. Keeps the spec DOM-free. */
export interface CmdkKeyEvent {
  readonly key: string;
  /** Physical code — `' '` and `'Space'` disagree across browsers for ⌥Space. */
  readonly code?: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

/**
 * Everything the decision depends on. All of it is cheap to read, and passing
 * it explicitly is what keeps `mapKey` pure.
 */
export interface CmdkKeyContext {
  /** The action-menu popover is open — it owns arrows, Enter and Left. */
  readonly actionMenuOpen: boolean;
  /** A flow page is on top of the stack — the results keymap is suspended. */
  readonly inFlow: boolean;
  /** Voice dictation is active. */
  readonly listening: boolean;
  /** Query, prefix excluded, is empty. */
  readonly queryEmpty: boolean;
  /** A prefix pill is showing. */
  readonly hasPrefix: boolean;
  /** Caret is at the very end of the input with nothing selected. */
  readonly caretAtEnd: boolean;
  /** The user has selected text inside the input. */
  readonly hasTextSelection: boolean;
  /** A ghost completion is currently offered. */
  readonly hasGhost: boolean;
  /** At least one selectable row exists and one is selected. */
  readonly hasSelection: boolean;
  /** The tenant has AI. When false, ask / continue-in-chat are inert. */
  readonly aiEnabled: boolean;
  /** An answer is present in the preview pane. */
  readonly hasAnswer: boolean;
  /** Voice is usable on this platform. When false, ⌥Space is inert. */
  readonly voiceSupported: boolean;
}

const isMod = (e: CmdkKeyEvent): boolean => e.metaKey || e.ctrlKey;

const isSpace = (e: CmdkKeyEvent): boolean => e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar';

/**
 * Map a keystroke to an intent, or null to let it through to the input.
 *
 * Order is significant and is asserted by the spec:
 *  1. The action menu, while open, owns its own keys outright.
 *  2. Escape, always, from every layer.
 *  3. Modifier chords, before bare keys — so ⌘↵ never falls through to ↵.
 *  4. In a flow, only the chrome keys survive; the flow owns the rest.
 *  5. Bare navigation and editing keys.
 */
export function mapKey(e: CmdkKeyEvent, ctx: CmdkKeyContext): CmdkIntent | null {
  // ── 1. Action menu has priority while open ──
  //
  // It claims only UNMODIFIED keys. A chord that reaches here is swallowed
  // rather than reinterpreted: ⌘↵ with the menu open must not quietly ask the
  // AI, and it must not silently pick whichever menu row happens to be
  // highlighted either. Doing nothing is the only reading that cannot surprise.
  if (ctx.actionMenuOpen) {
    if (e.key === 'Escape') return { type: 'close-action-menu' };
    if (isMod(e) || e.altKey || e.shiftKey) return null;
    if (e.key === 'ArrowLeft') return { type: 'close-action-menu' };
    if (e.key === 'ArrowDown') return { type: 'action-menu-move', delta: 1 };
    if (e.key === 'ArrowUp') return { type: 'action-menu-move', delta: -1 };
    if (e.key === 'Enter') return { type: 'action-menu-activate' };
    return null;
  }

  // ── 2. Escape, from any layer. The ladder is resolved by the state service ──
  if (e.key === 'Escape') return { type: 'escape' };

  // ── 3. Modifier chords ──
  if (isMod(e)) {
    // ⌘K / ⌘P close the palette rather than re-toggling it open. The service
    // suspends HotkeysService while open, so this is the ONLY handler for them.
    const lower = e.key.toLowerCase();
    if (lower === 'k' || lower === 'p') return { type: 'close-palette' };

    // ⌘J opens Ask on ordinary employee surfaces.
    // HotkeysService is suspended while the palette owns focus, so the
    // palette must claim the same chord itself rather than expecting the
    // global binding to fire underneath the modal.
    if (lower === 'j' && !e.shiftKey) {
      return ctx.aiEnabled ? { type: 'route-ai' } : null;
    }

    if (e.key === 'Enter') {
      if (!ctx.aiEnabled) return null;
      return e.shiftKey ? { type: 'continue-in-chat' } : { type: 'ask' };
    }

    // ⌘. gives the answer more room, and takes it back.
    if (e.key === '.') {
      return ctx.hasAnswer ? { type: 'toggle-answer-room' } : null;
    }

    // ⌘C copies a link to the selected result — but only when the user is not
    // copying text out of the input, which is the more literal reading of ⌘C.
    if (lower === 'c') {
      if (ctx.hasTextSelection || !ctx.hasSelection) return null;
      return { type: 'copy-link' };
    }

    // ⌘1–9 quick-open. ⌃1–9 works too, because ⌘1–9 collides with browser tab
    // switching outside the packaged app.
    if (/^[1-9]$/.test(e.key)) {
      if (ctx.inFlow) return null;
      return { type: 'quick-open', index: Number(e.key) - 1 };
    }

    return null;
  }

  // ── ⌥Space toggles dictation, from search or a flow ──
  if (e.altKey && isSpace(e)) {
    return ctx.voiceSupported ? { type: 'toggle-voice' } : null;
  }

  // ── 4. Inside a flow the page owns everything else ──
  if (ctx.inFlow) return null;

  // ── 5. Bare keys ──
  switch (e.key) {
    case 'ArrowDown':
      return { type: 'move', delta: 1 };
    case 'ArrowUp':
      return { type: 'move', delta: -1 };
    case 'PageDown':
      return { type: 'move', delta: 5 };
    case 'PageUp':
      return { type: 'move', delta: -5 };

    // Home/End only steal the caret when there is nothing to type through.
    // With a query present they must still move the text cursor.
    case 'Home':
      return ctx.queryEmpty ? { type: 'move-edge', edge: 'first' } : null;
    case 'End':
      return ctx.queryEmpty ? { type: 'move-edge', edge: 'last' } : null;

    case 'ArrowRight':
      if (!ctx.caretAtEnd) return null;
      if (ctx.hasGhost) return { type: 'accept-ghost' };
      return ctx.hasSelection ? { type: 'open-action-menu' } : null;

    case 'Tab':
      // ⇧Tab opens the action menu for the selection; plain Tab cycles scope.
      if (e.shiftKey) {
        return ctx.hasSelection ? { type: 'open-action-menu' } : { type: 'cycle-scope', delta: -1 };
      }
      return { type: 'cycle-scope', delta: 1 };

    case 'Enter':
      if (e.shiftKey) return { type: 'instant-book' };
      return ctx.hasSelection ? { type: 'activate' } : null;

    case 'Backspace':
      // First Backspace on an empty query drops the prefix pill; a second one
      // is left alone so the field behaves like a field.
      if (ctx.queryEmpty && ctx.hasPrefix) return { type: 'clear-prefix' };
      return null;

    case '?':
      return ctx.queryEmpty ? { type: 'help' } : null;

    default:
      return null;
  }
}

/**
 * A returned intent always means the palette has claimed the keystroke, so the
 * caller `preventDefault()`s unconditionally. Letting the browser also act is
 * how ⌘1 both quick-opens a result and switches browser tab.
 *
 * Keys the palette does NOT claim return null from `mapKey` and reach the
 * input untouched, which is what keeps ordinary typing ordinary.
 */
